import { Boss, type BossDef } from './boss-base';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';

/**
 * B12 — Mirror. A spectral echo that COPIES the player's movement on
 * a delay (the ghost retraces where you were ~2s ago) and REFLECTS a share of
 * the shots that hit it straight back at you. Fights your own habits: stand
 * still and the echo lines up its volley on your past position. docs/02 pool.
 * Signatures: delayed echo + shot reflection (always), aimed shard fan (P1/P2).
 */
export const MIRROR: BossDef = {
  id: 'mirror',
  nameKey: 'boss.mirror',
  tier: 3,
  minRoom: 1,
  baseHp: 1250,
  radius: 42,
  bodyKey: 'boss/mirror/body',
  movement: 'orbit', // unused — follows the player's delayed echo
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          type: 'aimed', bullet: 'bullet/enemy/shard', count: 3, spreadDeg: 18,
          interval: 2.4, telegraph: 0.35, speed: 250, radius: 10, damage: 1,
        },
      ],
    },
    {
      hpPct: 0.55,
      speedMul: 1.2,
      patterns: [
        {
          type: 'aimed', bullet: 'bullet/enemy/shard', count: 5, spreadDeg: 30,
          interval: 2.2, telegraph: 0.3, speed: 265, radius: 10, damage: 1,
        },
        {
          type: 'radial', bullet: 'bullet/enemy/ring', count: 8, interval: 3.4,
          spinDeg: 16, speed: 120, radius: 10, damage: 2, unlockTier: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Shatter: a loose shard ring off the glass
      tier: 2,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/shard', count: 8, interval: 3.8,
        spinDeg: 22, speed: 115, radius: 10, damage: 1,
      },
    },
    {
      // Glint: a telegraphed single-shot burst once the mirror cracks
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/shard', count: 1, spreadDeg: 0,
        interval: 3.2, telegraph: 0.5, burst: 3, burstGap: 0.16,
        speed: 300, radius: 10, damage: 1,
      },
    },
  ],
};

const REFLECT_CD = 0.4; // min gap between bounced shots (caps the spam)
const REFLECT_SPEED = 240;
/** The ghost retraces the player's path this far (s) in the past */
const ECHO_DELAY = 2.0;
/** Path samples kept (one per logic step) — must cover ECHO_DELAY at 60 Hz */
const ECHO_CAP = 192;

export class MirrorBoss extends Boss {
  private ex: number;
  private ey: number;
  private lpx = 0;
  private lpy = 0;
  private reflectCd = 0;
  /** Rolling log of the player's path, fed back delayed (B12) — a fixed
   *  ring buffer, so the echo never allocates during the fight (rule 5) */
  private readonly trailT = new Float64Array(ECHO_CAP);
  private readonly trailX = new Float64Array(ECHO_CAP);
  private readonly trailY = new Float64Array(ECHO_CAP);
  private trailHead = 0;
  private trailCount = 0;
  private clock = 0;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(MIRROR, level, x, y, homeX, homeY);
    this.ex = homeX;
    this.ey = homeY;
    this.allowReinforce = false; // the echo IS the threat
  }

  /** Log the player's path; the echo target is its ~2s-old copy. */
  private track(dt: number, px: number, py: number): void {
    this.clock += dt;
    const w = this.trailHead;
    this.trailT[w] = this.clock;
    this.trailX[w] = px;
    this.trailY[w] = py;
    this.trailHead = (w + 1) % ECHO_CAP;
    this.trailCount = Math.min(ECHO_CAP, this.trailCount + 1);
    // Oldest → newest: keep the last sample at or before the delayed time
    const want = this.clock - ECHO_DELAY;
    const oldest = (this.trailHead - this.trailCount + ECHO_CAP) % ECHO_CAP;
    let pick = oldest;
    for (let k = 1; k < this.trailCount; k++) {
      const i = (oldest + k) % ECHO_CAP;
      if (this.trailT[i]! > want) break;
      pick = i;
    }
    this.ex = this.trailX[pick]!;
    this.ey = this.trailY[pick]!;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    this.track(dt, px, py);
    this.lpx = px;
    this.lpy = py;
    this.reflectCd -= dt;
    super.update(dt, px, py, bullets, telegraphs);
  }

  /** Retrace the player's delayed echo instead of an orbit path. */
  protected override move(dt: number, px: number, py: number): void {
    void px; void py;
    const k = Math.min(1, 4 * this.levelSpeedMul * dt);
    this.x += (this.ex - this.x) * k;
    this.y += (this.ey - this.y) * k;
  }

  /** Take damage normally, but bounce a share of the shots back. */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    const before = this.hp;
    const hits = bullets.collideCircle(true, this.x, this.y, this.def.radius, (dmg) => {
      this.takeDamage(dmg);
      if (this.reflectCd <= 0 && !this.dead) {
        this.reflectCd = REFLECT_CD;
        const d = Math.hypot(this.lpx - this.x, this.lpy - this.y) || 1;
        bullets.spawn({
          x: this.x, y: this.y,
          dirX: (this.lpx - this.x) / d, dirY: (this.lpy - this.y) / d,
          speed: REFLECT_SPEED * this.bulletSpeedMul, life: 4, radius: 10, damage: 1,
          friendly: false, texKey: 'bullet/enemy/shard', rotates: false,
        });
      }
    });
    return { hits, dealt: before - this.hp };
  }

  override render(alpha: number): void {
    super.render(alpha);
    // Faint shimmer — it is a reflection, never fully solid
    this.body.alpha = 0.82 + Math.sin(this.moveAngle * 8) * 0.08;
    this.moveAngle += 0.05;
  }
}
